import React, { useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from "@mui/material";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import { useGetProgramFolderQuery, useCreateProgramFolderMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

/**
 * Batch 46 — Searchable folder picker with inline "Create Folder" modal.
 *
 * Both the parent picker inside the modal and the outer Program Folder
 * picker render the same indented / breadcrumb-path option list so the
 * administrator can immediately see the destination. NULL parent means
 * "Top level (All Programs)" — the phrase "Library root" never appears.
 */
type FolderOpt = { id: string | null; label: string; depth: number };

export default function FolderPicker({
  value, onChange, disabled, initialParentId,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  initialParentId?: string | null;
}) {
  const { data, refetch } = useGetProgramFolderQuery({});
  const [createFolder, { isLoading: creating }] = useCreateProgramFolderMutation();
  const { showMessage, showErrorMessage } = useNotifier();

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newParent, setNewParent] = useState<FolderOpt | null>(null);

  const flat: FolderOpt[] = useMemo(() => {
    const out: FolderOpt[] = [{ id: null, label: "Top level (All Programs)", depth: 0 }];
    const walk = (list: any[], depth: number, path: string[]) => {
      for (const f of list ?? []) {
        const nextPath = [...path, f.folderName];
        out.push({ id: String(f.id), label: nextPath.join(" / "), depth });
        if (f.children?.length) walk(f.children, depth + 1, nextPath);
      }
    };
    walk(Array.isArray(data?.data) ? (data!.data as any[]) : [], 0, []);
    return out;
  }, [data]);

  const current = flat.find((o) => (o.id ?? "") === (value ?? "")) ?? flat[0];

  const openCreate = () => {
    // Preselect: initialParentId when provided, else the currently-selected folder,
    // else Top level.
    const preselectId = initialParentId !== undefined ? initialParentId : value;
    const match = flat.find((o) => o.id === preselectId) ?? flat[0];
    setNewParent(match);
    setCreateOpen(true);
  };

  const submit = async () => {
    if (!newName.trim()) return;
    try {
      const res: any = await createFolder({
        folderName: newName.trim(),
        parentFolderId: newParent?.id ?? null,
      }).unwrap();
      if (res?.status) {
        showMessage(res?.message || "Folder created.");
        await refetch();
        const newId = res?.data?.id ? String(res.data.id) : null;
        if (newId) onChange(newId); // auto-select in Program form
        setCreateOpen(false);
        setNewName(""); setNewParent(null);
      } else {
        showErrorMessage(res?.message || "Failed to create folder.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to create folder.");
    }
  };

  return (
    <>
      <Autocomplete
        size="small"
        options={flat}
        value={current}
        onChange={(_, v: FolderOpt | null) => onChange(v?.id ?? null)}
        getOptionLabel={(o) => o?.label ?? ""}
        isOptionEqualToValue={(a, b) => (a?.id ?? null) === (b?.id ?? null)}
        disabled={disabled}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ pl: 2 + option.depth * 2 }}>
            {option.id === null ? <strong>{option.label}</strong> : option.label}
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Program Folder"
            placeholder="Select a folder or keep in All Programs" />
        )}
        noOptionsText="No folders match. Use “Create New Folder”."
      />
      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
        <Button size="small" startIcon={<CreateNewFolderIcon />}
          onClick={openCreate} disabled={disabled}>
          Create New Folder
        </Button>
      </Stack>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField autoFocus fullWidth size="small" label="Folder Name"
              value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />

            <Autocomplete size="small"
              options={flat}
              value={newParent ?? flat[0]}
              onChange={(_, v: FolderOpt | null) => setNewParent(v ?? flat[0])}
              getOptionLabel={(o) => o?.label ?? ""}
              isOptionEqualToValue={(a, b) => (a?.id ?? null) === (b?.id ?? null)}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ pl: 2 + option.depth * 2 }} data-testid="parent-option">
                  {option.id === null ? <strong>{option.label}</strong> : option.label}
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Parent Folder"
                  placeholder="Select where this folder should be created" />
              )}
            />
            <Typography variant="caption" color="text.secondary">
              Choose <strong>Top level (All Programs)</strong> to create a main folder, or select an existing folder to create a subfolder inside it.
              Your Program draft stays intact — this modal closes when the folder is created and auto-selects it.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={!newName.trim() || creating}>
            {creating ? "Creating…" : "Create Folder"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
